import { useState } from "react";
import {
  ChevronIcon,
  CloseIcon,
  PlusIcon,
} from "./Icons";
import type {
  PlaybookMaterial,
  PlaybookPosition,
} from "./types";
import "./review.css";

export type PlaybookEditorMode = "blank" | "existing" | "precedent" | "workflow";

export interface PlaybookPositionsEditorProps {
  title?: string;
  subtitle?: string;
  mode?: PlaybookEditorMode;
  positions: PlaybookPosition[];
  materials?: PlaybookMaterial[];
  sourcePlaybookName?: string;
  busy?: boolean;
  onBack?: () => void;
  onTest?: () => void;
  onSaveDraft?: () => void | Promise<void>;
  onPublish?: () => void | Promise<void>;
  onUpsertPosition?: (position: PlaybookPosition) => void | Promise<void>;
  onDraftPositions?: (description: string) => void | Promise<void>;
}

export function PlaybookPositionsEditor({
  title = "New playbook — draft",
  subtitle,
  mode = "workflow",
  positions,
  materials = [],
  sourcePlaybookName,
  busy = false,
  onBack,
  onTest,
  onSaveDraft,
  onPublish,
  onUpsertPosition,
  onDraftPositions,
}: PlaybookPositionsEditorProps) {
  const [editTarget, setEditTarget] = useState<PlaybookPosition | null | "new">(null);
  const [draftDescription, setDraftDescription] = useState("");
  const [publishConfirm, setPublishConfirm] = useState(false);
  const materialCount = materials.length;
  const effectiveSubtitle = subtitle || (
    mode === "workflow"
      ? `Drafted by the creation workflow from ${materialCount} material${materialCount === 1 ? "" : "s"}`
      : mode === "existing"
        ? `Copied from ${sourcePlaybookName || "a published playbook"}`
        : mode === "precedent"
          ? "Derived from a signed precedent"
          : "Blank — no positions yet"
  );

  return (
    <section className="di-review di-playbook-editor" aria-label={title}>
      <div className="di-playbook-editor__main">
        <div className="di-table-toolbar">
          <button
            className="di-btn di-btn--ghost"
            disabled={!onBack}
            onClick={onBack}
            title={!onBack ? "Back navigation is not connected." : undefined}
            type="button"
          >
            <ChevronIcon direction="left" />
            All playbooks
          </button>
          <span className="di-tag">Draft — private to you</span>
          <span className="di-spacer" />
          <button
            className="di-btn di-btn--secondary"
            disabled={!onTest || busy}
            onClick={onTest}
            title={!onTest ? "Playbook testing is not connected." : undefined}
            type="button"
          >
            Test on a document
          </button>
          <button
            className="di-btn di-btn--secondary"
            disabled={!onSaveDraft || busy}
            onClick={() => void onSaveDraft?.()}
            title={!onSaveDraft ? "Draft saving is not connected." : undefined}
            type="button"
          >
            Save draft
          </button>
          <button
            className="di-btn di-btn--primary"
            disabled={!onPublish || busy}
            onClick={() => setPublishConfirm(true)}
            title={!onPublish ? "Publishing is not connected." : undefined}
            type="button"
          >
            Publish
          </button>
        </div>
        <div className="di-playbook-editor__content">
          <div className="di-editor-title">
            <h1>{title}</h1>
            <p>{effectiveSubtitle}</p>
          </div>
          <h2 className="di-kicker di-kicker--ink">Positions</h2>
          {positions.length === 0 ? (
            <div className="di-empty-state">
              <strong>No positions yet</strong>
              <span>
                Add them one at a time, or describe your standard in the panel
                and let DI draft the first set for you.
              </span>
            </div>
          ) : (
            <div className="di-playbook-positions-scroll">
              <table className="di-simple-table di-positions-table">
                <caption className="di-visually-hidden">Negotiating positions</caption>
                <thead>
                  <tr>
                    <th scope="col">No.</th>
                    <th scope="col">Position</th>
                    <th scope="col">Our standard</th>
                    <th scope="col">Approved fallback</th>
                    <th scope="col">Escalation</th>
                    <th scope="col">Origin</th>
                  </tr>
                </thead>
                <tbody>
                  {positions.map((position) => (
                    <tr key={position.id}>
                      <td className="di-muted">{position.number}</td>
                      <td>
                        {onUpsertPosition ? (
                          <button
                            className="di-position-link"
                            onClick={() => setEditTarget(position)}
                            type="button"
                          >
                            {position.name}
                          </button>
                        ) : (
                          position.name
                        )}
                      </td>
                      <td>{position.standard}</td>
                      <td>{position.fallback}</td>
                      <td>{position.escalation}</td>
                      <td><span className="di-tag di-tag--accent">{position.origin}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <button
            className="di-btn di-btn--secondary di-add-position"
            disabled={!onUpsertPosition}
            onClick={() => setEditTarget("new")}
            title={!onUpsertPosition ? "Position editing is not connected." : undefined}
            type="button"
          >
            <PlusIcon />
            Add position
          </button>
          <p className="di-footnote">
            Each position records the standard, the fallback you will accept
            without asking, and who approves an exception. DI applies them in
            order when it redlines a counterparty draft.
          </p>
        </div>
      </div>

      <aside className="di-playbook-editor__aside">
        <h2 className="di-kicker">{modeLabel(mode)}</h2>
        {mode === "blank" && (
          <>
            <p className="di-muted">
              Nothing has been copied in. Describe the standard you negotiate
              to and DI will propose the first positions for you to edit.
            </p>
            <textarea
              className="di-input di-textarea"
              onChange={(event) => setDraftDescription(event.target.value)}
              placeholder="We cap liability at 12 months' fees, never accept arbitration, and require our DPA for any processing of personal data…"
              value={draftDescription}
            />
            <button
              className="di-btn di-btn--primary di-btn--block"
              disabled={!onDraftPositions || !draftDescription.trim()}
              onClick={() => void onDraftPositions?.(draftDescription.trim())}
              title={!onDraftPositions ? "AI position drafting is not connected." : undefined}
              type="button"
            >
              Draft positions
            </button>
          </>
        )}
        {mode === "workflow" && (
          <>
            <p className="di-muted">
              Drafted from the materials you provided. Where a precedent was
              more favourable than your old playbook, the better position became
              the standard and common alternatives became fallbacks.
            </p>
            <MaterialSummary materials={materials} emptyBaseline />
            <p className="di-footnote">
              Significant outliers were excluded rather than treated as
              fallbacks. Review each rule before publishing.
            </p>
          </>
        )}
        {mode === "existing" && (
          <>
            <p className="di-muted">
              Positions were copied from a published playbook. Edit them here —
              the source playbook is unaffected.
            </p>
            <div className="di-source-card">
              <span className="di-kicker">Copied from</span>
              <strong>{sourcePlaybookName || "Published playbook"}</strong>
              <span>{positions.length} positions shown</span>
            </div>
            <fieldset className="di-filter-group">
              <legend>Copy scope</legend>
              {[
                "Standards and fallbacks",
                "Standards only",
                "Everything, including escalations",
              ].map((option, index) => (
                <label className="di-radio" key={option}>
                  <input defaultChecked={index === 0} name="copy-scope" type="radio" />
                  <span aria-hidden="true" />
                  {option}
                </label>
              ))}
            </fieldset>
          </>
        )}
        {mode === "precedent" && (
          <>
            <p className="di-muted">
              DI read the signed precedent and proposed a position for each
              negotiated term, citing the clause it came from. Confirm the ones
              that reflect your standard.
            </p>
            <MaterialSummary materials={materials} />
          </>
        )}
      </aside>

      {editTarget && (
        <PositionDialog
          initial={editTarget === "new" ? undefined : editTarget}
          onClose={() => setEditTarget(null)}
          onSave={async (position) => {
            await onUpsertPosition?.(position);
            setEditTarget(null);
          }}
        />
      )}
      {publishConfirm && (
        <ConfirmPublish
          busy={busy}
          onCancel={() => setPublishConfirm(false)}
          onConfirm={async () => {
            await onPublish?.();
            setPublishConfirm(false);
          }}
          positionCount={positions.length}
        />
      )}
    </section>
  );
}

function modeLabel(mode: PlaybookEditorMode) {
  if (mode === "workflow") return "Drafted by the creation workflow";
  if (mode === "existing") return "Copied playbook";
  if (mode === "precedent") return "From a signed precedent";
  return "Blank playbook";
}

function MaterialSummary({
  materials,
  emptyBaseline = false,
}: {
  materials: PlaybookMaterial[];
  emptyBaseline?: boolean;
}) {
  if (materials.length === 0) {
    return (
      <p className="di-muted">
        {emptyBaseline
          ? "You skipped every question, so these are DI's baseline rules. Upload a playbook or precedents later and DI will refine them."
          : "No source material is attached yet."}
      </p>
    );
  }
  return (
    <>
      <h3 className="di-kicker di-kicker--ink">Materials used</h3>
      <div className="di-material-list">
        {materials.map((material) => (
          <div key={material.id}>
            <strong>{material.name}</strong>
            <span>{material.meta || "Source material"}</span>
          </div>
        ))}
      </div>
    </>
  );
}

function PositionDialog({
  initial,
  onClose,
  onSave,
}: {
  initial?: PlaybookPosition;
  onClose: () => void;
  onSave: (position: PlaybookPosition) => void | Promise<void>;
}) {
  const [position, setPosition] = useState<PlaybookPosition>(initial || {
    id: `position-${Date.now()}`,
    number: "",
    name: "",
    standard: "",
    fallback: "",
    escalation: "",
    origin: "Manual",
  });
  const headingId = "di-position-dialog-title";
  const valid = [
    position.number,
    position.name,
    position.standard,
    position.fallback,
    position.escalation,
  ].every((value) => value.trim());

  function patch(key: keyof PlaybookPosition, value: string) {
    setPosition((current) => ({ ...current, [key]: value }));
  }

  return (
    <div className="di-dialog-backdrop">
      <section
        aria-labelledby={headingId}
        aria-modal="true"
        className="di-dialog di-dialog--medium"
        role="dialog"
      >
        <header className="di-dialog__header">
          <h2 id={headingId}>{initial ? "Edit position" : "Add position"}</h2>
          <button aria-label="Close" className="di-icon-button" onClick={onClose} type="button">
            <CloseIcon />
          </button>
        </header>
        <div className="di-dialog__body di-position-form">
          {([
            ["number", "Position number"],
            ["name", "Position"],
            ["standard", "Our standard"],
            ["fallback", "Approved fallback"],
            ["escalation", "Escalation"],
            ["origin", "Origin"],
          ] as Array<[keyof PlaybookPosition, string]>).map(([key, label]) => (
            <label className="di-field" key={key}>
              <span>{label}</span>
              {["standard", "fallback"].includes(key) ? (
                <textarea
                  className="di-input"
                  onChange={(event) => patch(key, event.target.value)}
                  value={position[key]}
                />
              ) : (
                <input
                  className="di-input"
                  onChange={(event) => patch(key, event.target.value)}
                  value={position[key]}
                />
              )}
            </label>
          ))}
        </div>
        <footer className="di-dialog__footer">
          <span className="di-spacer" />
          <button className="di-btn di-btn--secondary" onClick={onClose} type="button">Cancel</button>
          <button
            className="di-btn di-btn--primary"
            disabled={!valid}
            onClick={() => void onSave(position)}
            type="button"
          >
            Save position
          </button>
        </footer>
      </section>
    </div>
  );
}

function ConfirmPublish({
  positionCount,
  busy,
  onCancel,
  onConfirm,
}: {
  positionCount: number;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
}) {
  const headingId = "di-publish-playbook-title";
  return (
    <div className="di-dialog-backdrop">
      <section
        aria-labelledby={headingId}
        aria-modal="true"
        className="di-dialog di-dialog--small"
        role="dialog"
      >
        <header className="di-dialog__header">
          <h2 id={headingId}>Publish playbook?</h2>
          <button aria-label="Close" className="di-icon-button" onClick={onCancel} type="button">
            <CloseIcon />
          </button>
        </header>
        <div className="di-dialog__body">
          <p>
            Publish {positionCount} position{positionCount === 1 ? "" : "s"} to
            the workspace. Review every standard, fallback and escalation route
            before continuing.
          </p>
        </div>
        <footer className="di-dialog__footer">
          <span className="di-spacer" />
          <button className="di-btn di-btn--secondary" disabled={busy} onClick={onCancel} type="button">
            Cancel
          </button>
          <button
            className="di-btn di-btn--primary"
            disabled={busy || positionCount === 0}
            onClick={() => void onConfirm()}
            type="button"
          >
            {busy ? "Publishing…" : "Publish"}
          </button>
        </footer>
      </section>
    </div>
  );
}
